import { useState } from "react";
import { motion } from "framer-motion";
import { Wallet, CalendarClock, Gift, TrendingUp } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { PlayButton } from "@/components/Bits";
import { APP_LINKS, PARTNER_IMG } from "@/data/site";

const PER_JOB = 320;
const DAYS = 26;

const BENEFITS = [
  { icon: Gift, title: "Zero joining fee", desc: "Bilkul free registration, koi hidden charge nahi." },
  { icon: CalendarClock, title: "Weekly payouts", desc: "Har week seedhe aapke bank account me payout." },
  { icon: TrendingUp, title: "Regular jobs", desc: "Aapke area me daily naye booking requests." },
];

export default function Partner() {
  const [jobs, setJobs] = useState(6);
  const monthly = (jobs * PER_JOB * DAYS).toLocaleString("en-IN");

  return (
    <section id="partner" className="relative py-20 sm:py-28 overflow-hidden" data-testid="become-partner-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* left */}
        <div>
          <Reveal className="flex items-center gap-2 text-azo-primary font-mono2 text-xs uppercase tracking-widest mb-4">
            <span className="h-px w-8 bg-azo-primary" /> 04 — Earn with us
          </Reveal>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            <MaskedLines lines={[<span key="l1">Apne hunar se</span>, <span key="l2" className="text-gradient">roz kamao.</span>]} />
          </h2>
          <Reveal delay={0.15} className="mt-5 max-w-lg text-azo-muted text-base leading-relaxed">
            Electrician, plumber, carpenter, beautician ya koi bhi skilled professional — AzoApp Partner
            ban kar apne sheher me kaam paao aur ek reliable, bharosemand income banao.
          </Reveal>

          {/* calculator */}
          <div className="mt-8 rounded-3xl border border-azo-border bg-azo-surface p-6 sm:p-7 shadow-azo" data-testid="partner-earning-calculator">
            <div className="flex items-center gap-2 text-sm text-azo-muted">
              <Wallet size={16} className="text-azo-primary" /> Earning calculator
            </div>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <p className="text-xs font-mono2 uppercase tracking-widest text-azo-muted">Est. monthly earning</p>
                <p className="font-heading font-extrabold text-4xl sm:text-5xl text-azo-primary mt-1" data-testid="partner-earning-value">
                  ₹{monthly}
                </p>
              </div>
              <span className="rounded-full bg-azo-primary/12 text-azo-primary text-sm font-semibold px-3 py-1">{jobs} jobs/day</span>
            </div>
            <input
              type="range"
              min={2}
              max={10}
              value={jobs}
              onChange={(e) => setJobs(Number(e.target.value))}
              data-testid="partner-earning-slider"
              className="mt-6 w-full accent-azo-primary cursor-pointer"
              aria-label="Jobs per day"
            />
            <div className="flex justify-between text-xs text-azo-muted mt-1 font-mono2">
              <span>2 jobs/day</span>
              <span>10 jobs/day</span>
            </div>
          </div>

          <div className="mt-7">
            <PlayButton
              href={APP_LINKS.partner}
              label="JOIN AS PARTNER"
              store="Download Partner App"
              variant="solid"
              testid="btn-download-partner-app"
            />
          </div>
        </div>

        {/* right visual + benefits */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden border border-azo-border shadow-azo aspect-[4/3] bg-azo-surface2"
          >
            <img src={PARTNER_IMG} alt="AzoApp partner earning" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <p className="font-mono2 text-[11px] uppercase tracking-widest opacity-70">Meet our partners</p>
              <p className="font-heading font-bold text-xl">300+ pros already earning</p>
            </div>
          </motion.div>

          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-2xl border border-azo-border bg-azo-surface p-4"
              >
                <b.icon size={20} className="text-azo-primary" />
                <p className="mt-3 font-heading font-semibold text-sm">{b.title}</p>
                <p className="mt-1 text-xs text-azo-muted leading-snug">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
